import { useEffect, useState } from 'react';

import { useLayoutServicePlaceholder } from 'hooks';
import type {
	Link as LinkType,
	MainLink,
	MainLinkFields,
	PageHeaderFields,
} from 'models/sitecore';
import type { ColorString } from 'types';
import { empty, is } from 'utils/helpers';

export const SCROLL_LOCK_WHITELIST_CLASS = 'page-header-scroll-child';

export function getStripeGradient(colors: ColorString[]): string {
	const percentage = 100 / colors.length;
	const stop = (color: ColorString, step: number) =>
		// Slice to remove unnecessary decimals.
		`${color} ${String(step * percentage).slice(0, 5)}%`;
	const stops = colors
		.map((color, i) => `${stop(color, i)}, ${stop(color, i + 1)}`)
		.join(', ');
	return `linear-gradient(to right, ${stops})`;
}

/**
 * Get the maximum nesting level for a list of links.
 */
function getMaxLevel(allLinks: MainLinkFields[], _max: number = 1): number {
	let newMax = 0;
	for (const link of allLinks) {
		if (link.links) {
			newMax = getMaxLevel(link.links, _max + 1);
		}
	}

	return Math.max(_max, newMax);
}

/**
 * Check if a list of links for a dropdown will need a large one.
 *
 * In practice this is currenly the product dropdown.
 */
export function isLargeDropdown(allLinks?: MainLinkFields[]): boolean {
	return allLinks ? getMaxLevel(allLinks) > 1 : false;
}

export function getLinkListId(baseId: string, linkId: string | undefined) {
	return linkId ? `${baseId}-link-list-${linkId}` : undefined;
}

export function isLinkVisible({ fields }: LinkType): boolean {
	return Boolean(fields?.link?.href);
}

function isMainLinkVisible({ fields }: MainLink): boolean {
	return Boolean(
		(fields.link ?? fields.links) && (fields.title || fields.link?.text),
	);
}

/**
 * Get the parents → children 'path' to the specified link ID.
 *
 * @example
 *
 * // Real objects will have more data.
 * const allLinks = [
 *   {
 *     id: '1',
 *     text: 'One',
 *     links: [{ id: '1.1', text: 'One-1' }, { id: '1.2', text: 'One-2' }]
 *   },
 *   {
 *     id: '2',
 *     text: 'Two',
 *     links: [{ id: '2.1', text: 'Two-1' }, { id: '2.2', text: 'Two-2' }]
 *   },
 * ];
 *
 * getLinkPath('1', allLinks);
 * // => [{ id: '1', text: 'One', links: [...] }]
 *
 * getLinkPath('2.2', allLinks);
 * // => [{ id: '2', text: 'Two', links: [...] }, { id: '2.2', text: 'Two-2' }]
 *
 * getLinkPath('3', allLinks);
 * // => []
 */
export function getLinkPath(
	linkId: string,
	allLinks: MainLinkFields[],
	_path: MainLinkFields[] = [],
): MainLinkFields[] {
	if (!linkId) {
		return [];
	}

	for (const link of allLinks) {
		if (link.id === linkId) {
			return [..._path, link];
		}
		if (link.links) {
			const childPath = getLinkPath(linkId, link.links, [..._path, link]);
			if (childPath[0]) {
				return childPath;
			}
		}
	}

	return [];
}

/**
 * Get the page header main menu.
 *
 * Caches main menu items globally to handle the menu being excluded from data
 * requests (see x-nextjs-data part in layout-service-factory.rest.ts).
 * The `mainMenu` field on PageHeader will have data from the initial browser
 * request but will be empty on any subsequent navigation requests. Caching
 * locally with `useRef` will not work since the entire header may be remounted
 * on navigation.
 * If the menu is missing from both the global cache and props, do a manual
 * fallback fetch to layout service.
 */
export function useMainMenu(mainMenuField: MainLink[] | undefined) {
	const [hasMainMenuFallback, setHasMainMenuFallback] = useState(false);
	const { component } = useLayoutServicePlaceholder<PageHeaderFields>(
		'jula-header',
		{
			componentName: 'PageHeader',
			isActive: hasMainMenuFallback,
		},
	);
	const mainMenuFallback = component?.mainMenu ?? empty.array;

	useEffect(() => {
		if (
			mainMenuField &&
			mainMenuField.length > 0 &&
			(!globalThis.mainMenuItems || globalThis.mainMenuItems.length === 0)
		) {
			globalThis.mainMenuItems = mainMenuField.filter(isMainLinkVisible);
		} else if (
			(!mainMenuField || mainMenuField.length === 0) &&
			(!globalThis.mainMenuItems || globalThis.mainMenuItems.length === 0) &&
			!hasMainMenuFallback
		) {
			setHasMainMenuFallback(true);
		}
	}, [hasMainMenuFallback, mainMenuField]);

	if (is.arrayWithLength(globalThis.mainMenuItems)) {
		return globalThis.mainMenuItems;
	}

	return (
		is.arrayWithLength(mainMenuField) ? mainMenuField : mainMenuFallback
	).filter(isMainLinkVisible);
}
